import { emitCustomEvent } from "react-custom-events";

const cachedItemPrefix = "dstv-ss";

export const LocalStorageHelper = {
    _accessKey: "access",
    _isBusinessKey: "isBusiness",
    _loggedInBusinessUser: "loggedInBusinessUser",
    _logKey: "log",
    _loggedInBusinessUserEventName: "CiamBusinessUserLoggedIn",
    _campaignDataKey: "campaignData",
    _validateSalesForceKey: "validate.sf",
    _returnUrlKey: "returnUrlSticky",

    _allKeys() {
        return [
            this._isBusinessKey
            , this._loggedInBusinessUser
            , this._accessKey
            , this._campaignDataKey
            , this._validateSalesForceKey
            , this._returnUrlKey
        ]
    },

    contains(key) {
        return this._getItem(key) != null;
    },

    _removeItem(key) {
        window.localStorage.removeItem(key);
    },

    _setItem(key, value) {
        window.localStorage.setItem(key, value);
    },

    _setItemAfterSerializing(key, value) {
        this._setItem(key, JSON.stringify(value));
    },

    _getItem(key, defaultValue = null) {
        const storedValue = window.localStorage.getItem(key);
        return storedValue == null ? defaultValue : storedValue;
    },

    _getParsedItem(key, defaultValue = null) {
        const storedValue = window.localStorage.getItem(key);
        try {
            return storedValue == null ? defaultValue : JSON.parse(storedValue);
        } catch {
            return defaultValue;
        }
    },

    _setBooleanValue(key, value) {
        this._setItem(key, value === true ? "true" : "false");
    },

    _getBooleanValue(key) {
        var value = this._getItem(key);
        return value != null && value === "true";
    },

    getAccess() {
        const accessValue = this._getItem(this._accessKey);
        return accessValue != null
            ? JSON.parse(accessValue)
            : null;
    },

    getAccessToken() {
        var token = this.getAccess();
        return token?.access_token;
    },

    isBusinessUser() {
        return this._getBooleanValue(this._isBusinessKey);
    },

    setIsBusinessUser(value) {
        this._setBooleanValue(this._isBusinessKey, value);
    },

    getLoggedInBusinessUser() {
        return this._getParsedItem(this._loggedInBusinessUser);
    },

    setLoggedInbusinessUser(value) {
        value = { ...value, businessName: value.agreements[0]?.friendlyName };
        this._setItemAfterSerializing(this._loggedInBusinessUser, value);
        emitCustomEvent(this._loggedInBusinessUserEventName, value);
    },

    getLoggedInUserCountryCode() {
        var access = LocalStorageHelper.getAccess();
        return access?.decodedIdToken?.country_code;
    },

    getCampaignData() {
        return this._getParsedItem(this._campaignDataKey);
    },

    setCampaignData(value) {
        this._setItemAfterSerializing(this._campaignDataKey, value);
    },

    getValidateSalesForce() {
        return this._getBooleanValue(this._validateSalesForceKey);
    },

    setValidateSalesForce(value) {
        this._setBooleanValue(this._validateSalesForceKey, value);
    },

    getReturnUrl() {
        return this._getItem(this._returnUrlKey);
    },

    setReturnUrl(value) {
        return this._setItem(this._returnUrlKey, value);
    },

    logout() {
        var keys = this._allKeys();
        for (var i = 0; i < keys.length; i++) {
            this._removeItem(keys[i]);
        }

        this.cleanUp();
    },

    //Used for special debugging
    log() {
        if (process.env.REACT_APP_LOGGING_TO_LOCAL_STORAGE !== "true") {
            return;
        }

        var numberOfArguments = arguments.length;

        switch (numberOfArguments) {
            case 0:
                return;

            default:
                this._log(arguments);
                break;
        }
    },

    _log(messages) {
        var currentLogValue = this._getItem(this._logKey);
        var currentLog = currentLogValue == null ? [] : JSON.parse(currentLogValue);

        for (let i = 0; i < messages.length; i++) {
            currentLog.push(messages[i]);
        }

        this._setItemAfterSerializing(this._logKey, currentLog);
    },

    clearLog() {
        this._removeItem(this._logKey);
    },

    //Removes all CachedItems
    cleanUp() {
        var ls = window.localStorage;

        for (let i = 0; i < ls.length; i++) {
            let key = ls.key(i);
            if (key.indexOf(cachedItemPrefix) === 0) {
                this._removeItem(key);
            }
        }
    }
}

export class CachedItem {
    _key;
    _cachedTimeKey;
    _shouldParse = false;
    _timeToLiveInSeconds = 0;

    constructor(key, shouldParse = true, timeToLive = 0, usePrefix = true) {
        this._key = usePrefix ? `${cachedItemPrefix}.${key}`: key;
        this._cachedTimeKey = `${this._key}.cached.time`;
        this._shouldParse = shouldParse;
        this._timeToLiveInSeconds = timeToLive;
    }

    static withoutPrefix(key, shouldParse = true, timeToLive = 0) {
        return new CachedItem(key, shouldParse, timeToLive, false);
    }

    _getExpiry() {
        let cachedTime = LocalStorageHelper._getItem(this._cachedTimeKey);
        cachedTime = cachedTime === null ? 0 : parseInt(cachedTime);

        return cachedTime === 0 ? 0 : cachedTime + (this._timeToLiveInSeconds * 1000);
    }

    isExpired() {
        if (this._timeToLiveInSeconds == 0) {
            return false;
        }

        let expiry = this._getExpiry();

        let time = new Date().getTime();

        return expiry === 0 || expiry < time;
    }

    isCached() {
        var cached = LocalStorageHelper.contains(this._key);
        return cached && !this.isExpired();
    }

    clear() {
        LocalStorageHelper._removeItem(this._key);
        LocalStorageHelper._removeItem(this._cachedTimeKey);
    }

    value = () => this.get();

    get() {
        return this._shouldParse
            ? LocalStorageHelper._getParsedItem(this._key)
            : LocalStorageHelper._getItem(this._key);
    }

    set(value) {
        let time = new Date().getTime();

        this._shouldParse
            ? LocalStorageHelper._setItemAfterSerializing(this._key, value)
            : LocalStorageHelper._setItem(this._key, value);

        if (this._timeToLiveInSeconds === 0) {
            return;
        }

        LocalStorageHelper._setItem(this._cachedTimeKey, time);
    }
}